/**
 * Lazy Loader Utility
 * 
 * Registers controllers dynamically based on data attributes in the DOM.
 * This helps reduce initial bundle size by avoiding loading controllers that
 * aren't used on the current page.
 * 
 * Usage:
 * 1. Add lazy controller registrations: lazyRegister('controller-name', () => import('./path/to/controller'))
 * 2. Call loadLazyControllers() after DOM is ready
 */

import { application } from './application'

interface LazyControllerConfig {
  identifier: string
  loader: () => Promise<{ default: any }>
}

const lazyControllers: LazyControllerConfig[] = []

export function registerLazy(identifier: string, loader: () => Promise<{ default: any }>) {
  lazyControllers.push({ identifier, loader })
}

export async function loadLazyControllers() {
  const elementsInDOM = new Set<string>()
  
  // Find all data-controller attributes in the DOM
  document.querySelectorAll('[data-controller]').forEach(el => {
    const controllers = el.getAttribute('data-controller')?.split(' ') || []
    controllers.forEach(controller => elementsInDOM.add(controller.trim()))
  })
  
  // Load only the controllers that are present in the DOM
  const loadPromises = lazyControllers
    .filter(config => elementsInDOM.has(config.identifier))
    .map(async config => {
      try {
        const module = await config.loader()
        application.register(config.identifier, module.default)
      } catch (error) {
        console.error(`Failed to load lazy controller "${config.identifier}":`, error)
      }
    })
  
  await Promise.all(loadPromises)
}
